import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { MessageSquare, Mail, Loader2, ReceiptText } from "lucide-react";

const SendReceiptDialog = ({ open, onClose, info, isDark, onSend }) => {
  const [via, setVia] = useState("sms");
  const [sending, setSending] = useState(false);

  const doc = info?.doc;
  const payment = info?.payment;
  const phone = doc?.customer_phone;
  const email = doc?.customer_email;

  useEffect(() => {
    if (open) setVia(phone ? "sms" : "email");
  }, [open, phone]);

  if (!doc || !payment) return null;

  const handleSend = async () => {
    setSending(true);
    try { await onSend(via); } finally { setSending(false); }
  };

  const optionCls = (active, disabled) =>
    `flex-1 rounded-xl border p-3 text-left transition-all ${
      disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"
    } ${
      active
        ? "border-emerald-500 bg-emerald-500/10"
        : isDark ? "border-zinc-700 hover:border-zinc-500" : "border-gray-200 hover:border-gray-400"
    }`;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-sm" data-testid="send-receipt-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ReceiptText className="w-5 h-5 text-emerald-500" /> Send Payment Receipt
          </DialogTitle>
          <DialogDescription>
            ${(payment.amount || 0).toFixed(2)} payment on {doc.invoice_number}
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2">
          <button
            type="button"
            disabled={!phone}
            onClick={() => phone && setVia("sms")}
            className={optionCls(via === "sms", !phone)}
            data-testid="receipt-via-sms"
          >
            <MessageSquare className="w-4 h-4 text-emerald-500 mb-1" />
            <p className="text-sm font-semibold text-foreground">SMS</p>
            <p className="text-[11px] text-muted-foreground truncate">{phone || "No phone on file"}</p>
          </button>
          <button
            type="button"
            disabled={!email}
            onClick={() => email && setVia("email")}
            className={optionCls(via === "email", !email)}
            data-testid="receipt-via-email"
          >
            <Mail className="w-4 h-4 text-blue-500 mb-1" />
            <p className="text-sm font-semibold text-foreground">Email</p>
            <p className="text-[11px] text-muted-foreground truncate">{email || "No email on file"}</p>
          </button>
        </div>

        <p className="text-[11px] text-muted-foreground">
          The receipt includes the payment amount, invoice total, paid-to-date and remaining balance.
        </p>

        <DialogFooter className="flex-col-reverse sm:flex-row gap-2 sm:gap-0">
          <Button type="button" variant="ghost" onClick={onClose} data-testid="send-receipt-cancel">Cancel</Button>
          <Button
            onClick={handleSend}
            disabled={sending || (!phone && !email)}
            className="bg-emerald-600 text-white hover:bg-emerald-700 gap-1.5"
            data-testid="send-receipt-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ReceiptText className="w-4 h-4" />}
            Send Receipt
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SendReceiptDialog;
